import { useState, useCallback } from "react"
import { Upload, X, Loader2, Music, AlertCircle, Youtube, Plus, Trash2 } from "lucide-react"
import { useDropzone } from "react-dropzone"

import { Button, Input, Label, Dialog, DialogContent, DialogHeader, DialogTitle, cn } from "@aix/ui"
import { trpc } from "../../../lib/trpc"

const GENRES = [
  { value: "bollywood", label: "Bollywood" },
  { value: "hip_hop", label: "Hip Hop" },
  { value: "pop", label: "Pop" },
  { value: "edm", label: "EDM" },
  { value: "latin", label: "Latin" },
  { value: "rnb", label: "R&B" },
  { value: "rock", label: "Rock" },
  { value: "country", label: "Country" },
  { value: "afrobeats", label: "Afrobeats" },
  { value: "kpop", label: "K-Pop" },
  { value: "reggaeton", label: "Reggaeton" },
  { value: "house", label: "House" },
  { value: "trap", label: "Trap" },
  { value: "drill", label: "Drill" },
] as const

type GenreValue = (typeof GENRES)[number]["value"]
type UploadMode = "file" | "youtube"

interface FileUpload {
  file: File
  title: string
  artist: string
  status: "pending" | "uploading" | "complete" | "error"
  error?: string
}

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadComplete: () => void
}

export function UploadModal({ isOpen, onClose, onUploadComplete }: UploadModalProps) {
  const [mode, setMode] = useState<UploadMode>("file")
  const [genre, setGenre] = useState<GenreValue>("pop")
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // File upload state
  const [files, setFiles] = useState<FileUpload[]>([])

  // YouTube state
  const [youtubeUrls, setYoutubeUrls] = useState<string[]>([""])
  const [youtubeProgress, setYoutubeProgress] = useState<{
    current: number
    total: number
    currentTitle?: string
  } | null>(null)

  const getUploadUrlMutation = trpc.mashupSong.getUploadUrl.useMutation()
  const completeUploadMutation = trpc.mashupSong.completeUpload.useMutation()
  const importFromYoutubeMutation = trpc.mashupSong.importFromYoutube.useMutation()
  const bulkImportMutation = trpc.mashupSong.bulkImportFromYoutube.useMutation()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles: FileUpload[] = acceptedFiles.map((file) => ({
      file,
      title: file.name.replace(/\.[^/.]+$/, ""),
      artist: "",
      status: "pending" as const,
    }))
    setFiles((prev) => [...prev, ...newFiles])
    setError(null)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/*": [".mp3", ".wav", ".m4a", ".aac", ".ogg", ".flac"],
    },
    maxSize: 200 * 1024 * 1024,
    multiple: true, // Enable multiple file selection
  })

  const updateFile = (index: number, updates: Partial<FileUpload>) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)))
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleFileUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one file")
      return
    }

    setIsUploading(true)
    setError(null)

    for (let i = 0; i < files.length; i++) {
      const fileUpload = files[i]
      if (fileUpload.status !== "pending") continue

      try {
        updateFile(i, { status: "uploading" })

        // Get upload URL
        const { uploadUrl, songId } = await getUploadUrlMutation.mutateAsync({
          fileName: fileUpload.file.name,
          contentType: fileUpload.file.type || "audio/mpeg",
          fileSize: fileUpload.file.size,
          title: fileUpload.title,
          artist: fileUpload.artist || undefined,
          genre,
        })

        // Upload to R2
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: fileUpload.file,
          headers: {
            "Content-Type": fileUpload.file.type || "audio/mpeg",
          },
        })

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`)
        }

        // Complete upload
        await completeUploadMutation.mutateAsync({ songId })

        updateFile(i, { status: "complete" })
      } catch (err: any) {
        console.error("Upload error:", err)
        updateFile(i, { status: "error", error: err.message || "Upload failed" })
      }
    }

    setIsUploading(false)

    // Check if any succeeded
    const hasSuccess = files.some((f) => f.status === "complete")
    if (hasSuccess) {
      onUploadComplete()
    }
  }

  const addYoutubeUrl = () => {
    if (youtubeUrls.length < 10) {
      setYoutubeUrls((prev) => [...prev, ""])
    }
  }

  const removeYoutubeUrl = (index: number) => {
    setYoutubeUrls((prev) => prev.filter((_, i) => i !== index))
  }

  const updateYoutubeUrl = (index: number, value: string) => {
    setYoutubeUrls((prev) => prev.map((url, i) => (i === index ? value : url)))
  }

  const handleYoutubeImport = async () => {
    const validUrls = youtubeUrls.filter((url) => url.trim().length > 0)

    if (validUrls.length === 0) {
      setError("Please enter at least one YouTube URL")
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      if (validUrls.length === 1) {
        // Single import
        setYoutubeProgress({ current: 1, total: 1, currentTitle: "Fetching..." })
        const result = await importFromYoutubeMutation.mutateAsync({
          youtubeUrl: validUrls[0],
          genre,
        })
        setYoutubeProgress({ current: 1, total: 1, currentTitle: result.title })
      } else {
        // Bulk import
        setYoutubeProgress({ current: 0, total: validUrls.length })
        const result = await bulkImportMutation.mutateAsync({
          urls: validUrls,
          genre,
        })

        if (result.failed > 0) {
          const failedUrls = result.results
            .filter((r) => !r.success)
            .map((r) => r.error || "Unknown error")
            .join(", ")
          setError(`${result.failed} of ${result.total} imports failed: ${failedUrls}`)
        }

        setYoutubeProgress({
          current: result.successful,
          total: result.total,
        })
      }

      // Reset and close
      setYoutubeUrls([""])
      onUploadComplete()
    } catch (err: any) {
      console.error("YouTube import error:", err)
      setError(err.message || "Failed to import from YouTube")
    } finally {
      setIsUploading(false)
      setYoutubeProgress(null)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      setFiles([])
      setYoutubeUrls([""])
      setGenre("pop")
      setError(null)
      setYoutubeProgress(null)
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Songs</DialogTitle>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex gap-2 border-b border-border">
          <button
            type="button"
            onClick={() => setMode("file")}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
              mode === "file"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Upload className="h-4 w-4 inline mr-2" />
            File Upload
          </button>
          <button
            type="button"
            onClick={() => setMode("youtube")}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
              mode === "youtube"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Youtube className="h-4 w-4 inline mr-2" />
            YouTube Import
          </button>
        </div>

        {/* Genre Selection (shared) */}
        <div className="space-y-2">
          <Label htmlFor="genre">Genre *</Label>
          <select
            id="genre"
            value={genre}
            onChange={(e) => setGenre(e.target.value as GenreValue)}
            className="w-full px-3 py-2 border border-border rounded-md bg-background"
            disabled={isUploading}
          >
            {GENRES.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>

        {/* File Upload Mode */}
        {mode === "file" && (
          <div className="space-y-4">
            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
              )}
            >
              <input {...getInputProps()} />
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? "Drop audio files here" : "Drag & drop audio files, or click to browse"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                MP3, WAV, M4A, AAC (max 200MB each) - Multiple files supported
              </p>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {files.map((fileUpload, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border",
                      fileUpload.status === "complete" && "border-green-500 bg-green-500/5",
                      fileUpload.status === "error" && "border-red-500 bg-red-500/5",
                      fileUpload.status === "uploading" && "border-blue-500 bg-blue-500/5",
                      fileUpload.status === "pending" && "border-border",
                    )}
                  >
                    <Music className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <Input
                        value={fileUpload.title}
                        onChange={(e) => updateFile(index, { title: e.target.value })}
                        placeholder="Title"
                        className="mb-1 h-8 text-sm"
                        disabled={fileUpload.status !== "pending" || isUploading}
                      />
                      <Input
                        value={fileUpload.artist}
                        onChange={(e) => updateFile(index, { artist: e.target.value })}
                        placeholder="Artist (optional)"
                        className="h-8 text-sm"
                        disabled={fileUpload.status !== "pending" || isUploading}
                      />
                      {fileUpload.status === "error" && <p className="text-xs text-red-500 mt-1">{fileUpload.error}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {fileUpload.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                      {fileUpload.status === "complete" && (
                        <span className="text-xs text-green-500 font-medium">Done</span>
                      )}
                      {fileUpload.status === "pending" && !isUploading && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeFile(index)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* YouTube Mode */}
        {mode === "youtube" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter YouTube URLs to import audio. The audio will be downloaded and processed server-side.
            </p>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {youtubeUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={url}
                    onChange={(e) => updateYoutubeUrl(index, e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    disabled={isUploading}
                    className="flex-1"
                  />
                  {youtubeUrls.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeYoutubeUrl(index)}
                      disabled={isUploading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {youtubeUrls.length < 10 && (
              <Button type="button" variant="outline" size="sm" onClick={addYoutubeUrl} disabled={isUploading}>
                <Plus className="h-4 w-4 mr-2" />
                Add Another URL
              </Button>
            )}

            {youtubeProgress && (
              <div className="text-sm text-muted-foreground">
                <p>
                  Importing {youtubeProgress.current} of {youtubeProgress.total}...
                </p>
                {youtubeProgress.currentTitle && <p className="text-xs">{youtubeProgress.currentTitle}</p>}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          {mode === "file" ? (
            <Button
              onClick={handleFileUpload}
              disabled={files.length === 0 || files.every((f) => f.status !== "pending") || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {files.filter((f) => f.status === "pending").length} File
                  {files.filter((f) => f.status === "pending").length !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          ) : (
            <Button onClick={handleYoutubeImport} disabled={youtubeUrls.every((url) => !url.trim()) || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Youtube className="h-4 w-4 mr-2" />
                  Import from YouTube
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
